import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Notification from "../../../../components/toast";
import reqFiscalConfig from "../../../../services/reqCobranca/reqFiscalConfig";
import { searchCep } from "../../../../util/libSearchCep";
import { onlyNumbers } from "../../../../util/libString";
import { schema } from "./schema";

export const AMBIENTES = [
	{ ambiente: "HOMOLOGACAO", nome: "Homologação" },
	{ ambiente: "PRODUCAO", nome: "Produção" },
];

export const REGIMES = [
	{ crt: 1, nome: "1 - Simples Nacional" },
	{ crt: 2, nome: "2 - Simples Nacional, excesso de receita" },
	{ crt: 3, nome: "3 - Regime Normal" },
];

const valoresIniciais = {
	cnpj: "",
	ie: "",
	razao_social: "",
	fantasia: "",
	telefone: "",
	cep: "",
	rua: "",
	numero: "",
	complemento: "",
	bairro: "",
	cod_cidade: "",
	cidade: "",
	uf: "",
	crt: 1,
	ambiente: "HOMOLOGACAO",
	serie: 1,
	csc: "",
	id_csc: "",
	timeout: 5000,
	cfop_padrao: "5102",
	csosn_padrao: "102",
	cst_padrao: "",
	icms_padrao: 0,
	ncm_padrao: "",
	cest_padrao: "",
	cst_ibscbs_padrao: "",
	cclasstrib_padrao: "",
	pibsuf: 0,
	pibsmun: 0,
	pcbs: 0,
};

// O servidor devolve null nas colunas vazias e o input reclama de null.
const semNulos = (dados) =>
	Object.keys(valoresIniciais).reduce(
		(acc, campo) => ({ ...acc, [campo]: dados[campo] ?? valoresIniciais[campo] }),
		{},
	);

const soDigitos = (valor) => onlyNumbers(String(valor ?? ""));

// O MaskInput guarda o valor com mascara. No banco e na nota vai so digito.
const semMascara = (dados) => ({
	...dados,
	cnpj: soDigitos(dados.cnpj),
	cep: soDigitos(dados.cep),
	telefone: soDigitos(dados.telefone),
	cod_cidade: soDigitos(dados.cod_cidade),
});

const lerArquivoBase64 = (arquivo) =>
	new Promise((resolve, reject) => {
		const leitor = new FileReader();

		leitor.onload = () => resolve(String(leitor.result).split(",")[1]);
		leitor.onerror = () => reject(new Error("Não foi possível ler o arquivo."));
		leitor.readAsDataURL(arquivo);
	});

// O interceptor do apisServer troca o erro do axios por { message, status },
// e joga o corpo cru da resposta em message: texto, { erros: [] } ou objeto
// inesperado - todos viram texto aqui.
const mensagemDoErro = (error) => {
	const corpo = error?.message;

	if (Array.isArray(corpo?.erros)) return corpo.erros.join(" ");

	if (typeof corpo === "string" && corpo.trim()) return corpo;

	if (corpo) return JSON.stringify(corpo);

	return "Não foi possível completar a operação.";
};

export const IndexHook = () => {
	const {
		register,
		handleSubmit,
		setValue,
		setFocus,
		getValues,
		control,
		reset,
		trigger,
		formState: { errors, isSubmitting },
	} = useForm({
		mode: "onSubmit",
		resolver: zodResolver(schema),
		defaultValues: valoresIniciais,
	});

	const [loading, setLoading] = useState(true);
	const [valueTab, setValueTab] = useState("0");
	const [certificado, setCertificado] = useState(null);
	const [arquivo, setArquivo] = useState(null);
	const [senha, setSenha] = useState("");
	const [enviandoCert, setEnviandoCert] = useState(false);
	const [divergencia, setDivergencia] = useState(null);
	const [erroCertificado, setErroCertificado] = useState(null);
	const [cepIsSubmitting, setCepIsSubmitting] = useState(false);

	const handleChangeTab = (event, novaAba) => setValueTab(novaAba);

	const fetch = async () => {
		try {
			setLoading(true);

			const { data } = await reqFiscalConfig.get();

			if (data && typeof data === "object") {
				reset(semNulos(data));

				setCertificado(
					data.tem_certificado
						? {
								titular: data.cert_titular,
								cnpj: data.cert_cnpj,
								validade: data.cert_validade,
								numero_serie: data.cert_numero_serie,
								bloqueado: data.bloqueado,
								bloqueio_motivo: data.bloqueio_motivo,
							}
						: null,
				);
			}
		} catch (error) {
			// 404 aqui e normal: empresa ainda sem configuracao.
			if (error?.status !== 404)
				Notification.showToast({ message: mensagemDoErro(error), type: "warning" });
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetch();
	}, []);

	const handleOnSubmit = async (data) => {
		try {
			await reqFiscalConfig.save(semMascara(data));

			Notification.showToast({ message: "Configuração fiscal salva.", type: "success" });
		} catch (error) {
			Notification.showToast({ message: mensagemDoErro(error), type: "error" });
		}
	};

	const handleFetchAddress = async () => {
		const cep = soDigitos(getValues("cep"));

		if (cep.length !== 8) {
			Notification.showToast({ message: "Digite um CEP válido.", type: "warning" });
			return;
		}

		try {
			setCepIsSubmitting(true);

			const data = await searchCep(cep);

			setValue("rua", data.logradouro.toUpperCase());
			setValue("bairro", data.bairro.toUpperCase());
			setValue("cidade", data.localidade.toUpperCase());
			setValue("uf", data.uf.toUpperCase());
			setValue("cod_cidade", data.ibge);
			setFocus("numero");
		} catch (error) {
			Notification.showToast({ message: error.message, type: "warning" });
		} finally {
			setCepIsSubmitting(false);
		}
	};

	// IE nao tem mascara: o formato muda por UF. So barra o que nao e digito.
	const handleDigitarIE = (event) => setValue("ie", soDigitos(event.target.value));

	const handleCancelarTroca = () => {
		setArquivo(null);
		setSenha("");
		setDivergencia(null);
		setErroCertificado(null);
	};

	const handleSelecionarArquivo = (event) => {
		setArquivo(event.target.files?.[0] ?? null);
		setDivergencia(null);
		setErroCertificado(null);
	};

	// O certificado traz CNPJ, razao social, cidade e UF. Preencher aqui
	// evita o usuario digitar justamente os campos que, errados, derrubam
	// toda nota.
	const preencherComCertificado = (dados) => {
		setValue("cnpj", dados.cnpj ?? "");
		setValue("razao_social", dados.titular ?? "");

		if (dados.cidade) setValue("cidade", dados.cidade);
		if (dados.uf) setValue("uf", dados.uf);
	};

	const enviarCertificado = async (confirmar) => {
		const base64 = await lerArquivoBase64(arquivo);
		const { data } = await reqFiscalConfig.uploadCertificado(base64, senha, confirmar);

		// Na troca de mesmo CNPJ o servidor so grava as colunas do certificado.
		// Preencher o formulario fora disso mostraria dado que nao esta no banco.
		if (confirmar || data.criou_cadastro) preencherComCertificado(data);

		setCertificado({ ...data, bloqueado: false, bloqueio_motivo: null });
		setArquivo(null);
		setSenha("");
		setDivergencia(null);

		Notification.showToast({
			message: data.criou_cadastro
				? "Certificado enviado e cadastro criado com os dados dele."
				: "Certificado enviado com sucesso.",
			type: "success",
		});
	};

	const handleEnviarCertificado = async (confirmar = false) => {
		if (!arquivo) {
			Notification.showToast({ message: "Escolha o arquivo .pfx.", type: "warning" });
			return false;
		}

		if (!senha) {
			Notification.showToast({ message: "Informe a senha do certificado.", type: "warning" });
			return false;
		}

		try {
			setEnviandoCert(true);
			setErroCertificado(null);

			await enviarCertificado(confirmar);
			return true;
		} catch (error) {
			if (error?.status === 409) {
				setDivergencia(error.message);
				return false;
			}

			setErroCertificado(mensagemDoErro(error));
			return false;
		} finally {
			setEnviandoCert(false);
		}
	};

	const fieldsByTab = {
		0: [],
		1: ["cnpj", "ie", "razao_social", "fantasia", "telefone", "crt", "cep", "rua", "numero", "complemento", "bairro", "cidade", "cod_cidade", "uf"],
		2: ["ambiente", "serie", "timeout", "id_csc", "csc"],
		3: ["cfop_padrao", "csosn_padrao", "cst_padrao", "icms_padrao", "ncm_padrao", "cest_padrao"],
		4: ["cst_ibscbs_padrao", "cclasstrib_padrao", "pibsuf", "pibsmun", "pcbs"],
	};

	const handleOnError = (errors) => {
		for (const tabIndex in fieldsByTab) {
			const fields = fieldsByTab[tabIndex];

			if (fields.some((field) => errors[field])) {
				setValueTab(tabIndex);
				break;
			}
		}
		Notification.showToast({
			message: "Preencha os campos obrigatórios na aba destacada antes de salvar.",
			type: "warning",
		});
	};

	const handleNextCertificado = async () => {
		if (arquivo && senha) {
			const sucesso = await handleEnviarCertificado(false);
			if (sucesso) setValueTab("1");
		} else {
			if (!certificado) {
				Notification.showToast({ message: "É necessário enviar um certificado.", type: "warning" });
				return;
			}
			
			const hoje = new Date();
			hoje.setHours(0, 0, 0, 0); 
			const validade = new Date(certificado.validade);
			validade.setHours(0, 0, 0, 0);
			
			if (validade < hoje) {
				Notification.showToast({ message: "Seu certificado está vencido. Envie um novo arquivo .pfx para continuar.", type: "warning" });
				return;
			}
			
			if (certificado.bloqueado) {
				Notification.showToast({ message: "Certificado bloqueado. Verifique o motivo na tela.", type: "warning" });
				return;
			}

			setValueTab("1");
		}
	};

	return {
		register,
		handleSubmit,
		handleOnSubmit,
		setValue,
		control,
		errors,
		isSubmitting,
		loading,

		valueTab,
		handleChangeTab,

		cepIsSubmitting,
		handleFetchAddress,
		handleDigitarIE,

		certificado,
		arquivo,
		senha,
		setSenha,
		enviandoCert,
		divergencia,
		erroCertificado,
		handleSelecionarArquivo,
		handleEnviarCertificado,
		handleCancelarTroca,
		
		handleOnError,
		handleNextCertificado,
	};
};
